/**
 * SegmentedControl — ряд ячеек-пилюль, собранный в один визуальный
 * контрол. Ячейка это `ChipMUI` с `rounded={false}`: скругление у ряда
 * одно общее, его держит контейнер, а между ячейками остаётся микро-гэп
 * (`spacing.tight` — токен ровно про это: «между близко поставленными
 * элементами с собственной заливкой»).
 *
 * У контрола ДВЕ независимые оси.
 *
 * 1. `mode` — ПОВЕДЕНИЕ, первые три один в один с `SegmentedButton`:
 *    - **radio**    — single-select (активна одна ячейка);
 *    - **checkbox** — multi-select (активен любой поднабор);
 *    - **button**   — без управляемого состояния, каждая ячейка сама;
 *    - **nav**      — якорная навигация: ячейки это ссылки, активная
 *      помечается `aria-current`. Не форма — выбора здесь нет, есть
 *      указание, где пользователь сейчас находится.
 *
 * 2. `variant` — ВИД ряда:
 *    - **segment** — общий радиус `radius.m` (8px), прямоугольный ряд;
 *    - **chip**    — радиус по формуле пилюли, `height / 2`: торцы ряда
 *      становятся полукруглыми, ровно как у одиночного `ChipMUI`.
 *
 * Обе оси ортогональны: chip-вид сочетается с любым поведением.
 *
 * Вид переопределяется на двух уровнях. Группа задаёт умолчания для всех
 * ячеек (`size`, `color`, `idleColor`, `idleVariant` / `surface`, `gap`,
 * `fullWidth`); отдельная ячейка перебивает их своими `color` / `variant`
 * — см. `ChipSegment`.
 *
 * Чем отличается от `SegmentedButton`: тот собран из собственных
 * прямоугольных ячеек по Figma-компоненту «Segmented Button»; этот — из
 * пилюль дизайн-системы, поэтому наследует всю их палитру, размерную
 * лестницу и состояния без единой своей GEO-таблицы.
 *
 * @format
 */

import {createContext, useMemo, type ReactNode} from 'react';

import {Box} from '@mui/material';
import {styled} from '@mui/material/styles';

import {
  CHIPMUI_SIZES,
  type ChipMUIColor,
  type ChipMUISize,
  type ChipMUIVariant,
} from 'ui/components/atoms/ChipMUI';
import {pillGeometry} from 'ui/theme/controlGeometry';
import {
  useSurfaceContext,
  type SurfaceContext as SurfaceContextValue,
} from 'ui/theme/surfaceContext';
import bordersTokens from 'ui/tokens/semantic/borders.json';
import {v} from 'ui/tokens/util';

const RADIUS_M_PX = parseInt(v(bordersTokens.radius.m));

/** Размерная лестница у контрола общая с пилюлей — ячейка и есть пилюля. */
export const SEGMENTED_CONTROL_SIZES = CHIPMUI_SIZES;
export type SegmentedControlSize = ChipMUISize;

/**
 * Вид ряда. `segment` — общий радиус 8px. `chip` — радиус `height / 2`,
 * как у одиночной пилюли: ряд читается одной длинной пилюлей с
 * полукруглыми торцами.
 */
export const SEGMENTED_CONTROL_VARIANTS = ['segment', 'chip'] as const;
export type SegmentedControlVariant = (typeof SEGMENTED_CONTROL_VARIANTS)[number];

/**
 * Фон незанятых ячеек — пресет для `idleVariant`. Ступени те же, что у
 * `SegmentedButton`, но выражены через варианты neutral-пилюли, а не
 * через свои заливки:
 *
 *   - `'surface-1'` — neutral soft. Контрол лежит на фоне страницы.
 *   - `'surface-2'` — neutral solid. Дефолт: контрол лежит на панели
 *     `surface-1` (FormCard и т.п.) и не сливается с ней.
 *   - `'ghost'`     — neutral outline. Прозрачные ячейки с волосяной
 *     обводкой у каждой; годится на произвольной поверхности.
 */
export const SEGMENTED_CONTROL_SURFACES = ['surface-1', 'surface-2', 'ghost'] as const;
export type SegmentedControlSurface = (typeof SEGMENTED_CONTROL_SURFACES)[number];

const SURFACE_VARIANT: Record<SegmentedControlSurface, ChipMUIVariant> = {
  'surface-1': 'soft',
  'surface-2': 'solid',
  ghost: 'outline',
};

/**
 * Зазор между ячейками. `tight` (1px) — дефолт и штатный микро-гэп
 * составного контрола; более широкие ступени превращают ряд в набор
 * различимых пилюль.
 */
export const SEGMENTED_CONTROL_GAPS = ['tight', 'xs', 's'] as const;
export type SegmentedControlGap = (typeof SEGMENTED_CONTROL_GAPS)[number];

// theme.spacing() при базе 4: 0.25 → 1px, 1 → 4px, 2 → 8px.
const GAP_FACTOR: Record<SegmentedControlGap, number> = {
  tight: 0.25,
  xs: 1,
  s: 2,
};

/**
 * Что делать, когда ячейки не влезают по ширине.
 *
 *   - `'none'`   — дефолт и текущее поведение: ряд не сжимается и не
 *     переносится, распирая родителя. Годится, пока ячеек мало.
 *   - `'scroll'` — собственный горизонтальный скролл. Ряд остаётся
 *     цельным контролом; выбор для узких вьюпортов.
 *   - `'wrap'`   — перенос на следующую строку. Ряд перестаёт быть одной
 *     сплошной пилюлей, поэтому скругление получает КАЖДАЯ ячейка: конец
 *     строки заранее не известен. Радиус при этом считается от высоты
 *     ОДНОЙ ячейки, а не от высоты выросшего контейнера.
 */
export const SEGMENTED_CONTROL_OVERFLOWS = ['none', 'scroll', 'wrap'] as const;
export type SegmentedControlOverflow = (typeof SEGMENTED_CONTROL_OVERFLOWS)[number];

// ── Контекст, общий с ячейками ─────────────────────────────────────────

export interface SegmentedControlContextValue {
  mode: 'radio' | 'checkbox' | 'button' | 'nav';
  size: SegmentedControlSize;
  /** Цвет ВЫБРАННОЙ ячейки. */
  color: ChipMUIColor;
  /** Цвет НЕЗАНЯТОЙ ячейки. */
  idleColor: ChipMUIColor;
  /** Вариант незанятой ячейки. */
  idleVariant: ChipMUIVariant;
  fullWidth: boolean;
  disabled: boolean;
  /** Должна ли ячейка с этим value рисоваться выбранной. */
  resolveSelected: (value: string | undefined) => boolean;
  /** Клик по ячейке. */
  handleClick: (value: string | undefined, ownOnClick?: () => void) => void;
}

export const SegmentedControlContext = createContext<SegmentedControlContextValue | null>(null);

// ── Публичные пропы (дискриминированный union — типобезопасно по режимам) ─

interface CommonGroupProps {
  /** Ячейки — список `<ChipSegment>`. */
  children: ReactNode;

  /** Вид ряда: `segment` (радиус 8px) или `chip` (радиус height / 2). */
  variant?: SegmentedControlVariant;

  /** Ступень размерной лестницы пилюли. Дефолт `m` (48). */
  size?: SegmentedControlSize;

  /** Цвет выбранной ячейки. Дефолт `primary`. */
  color?: ChipMUIColor;

  /** Цвет незанятых ячеек. Дефолт `neutral`. */
  idleColor?: ChipMUIColor;

  /**
   * Вариант незанятых ячеек. Если не задан — выводится из `surface`.
   * Задан — перебивает `surface`.
   */
  idleVariant?: ChipMUIVariant;

  /** Пресет фона незанятых ячеек. Дефолт `surface-2`. */
  surface?: SegmentedControlSurface;

  /** Зазор между ячейками. Дефолт `tight` (1px). */
  gap?: SegmentedControlGap;

  /** Поведение при нехватке ширины. Дефолт `none`. */
  overflow?: SegmentedControlOverflow;

  /** Растянуть ряд на всю ширину, ячейки равными долями. */
  fullWidth?: boolean;

  /** Выключить всю группу. */
  disabled?: boolean;

  /** Переопределение surface-контекста; наследуется от провайдера. */
  context?: SurfaceContextValue;
}

interface RadioProps extends CommonGroupProps {
  mode?: 'radio';
  /**
   * Выбранное значение. `null` — ничего не выбрано: законное состояние
   * группы до первого выбора. Пустая строка сюда тоже «сработает», но
   * случайно — она просто не совпадёт ни с одной ячейкой; `null`
   * говорит то же самое явно.
   */
  value: string | null;
  /** Срабатывает с новым значением при выборе другой ячейки. */
  onChange: (next: string) => void;
}

interface CheckboxProps extends CommonGroupProps {
  mode: 'checkbox';
  /** Выбранные значения. */
  value: string[];
  /** Срабатывает с обновлённым массивом при переключении. */
  onChange: (next: string[]) => void;
}

interface ButtonProps extends CommonGroupProps {
  mode: 'button';
  /**
   * У `button` нет управляемого состояния — каждая ячейка несёт свои
   * `onClick` и (по желанию) `selected`. Эти два поля намеренно
   * отсутствуют в этой ветке union'а.
   */
}

interface NavProps extends CommonGroupProps {
  mode: 'nav';
  /**
   * Значение ячейки, на которой пользователь сейчас находится. `null` —
   * ни одна: так и бывает, когда рядов несколько и текущее место
   * принадлежит соседнему, либо скролл-спай ещё не определился.
   */
  value: string | null;
  /**
   * Необязателен: ячейка со своим `href` переходит сама, нативно. Ставь
   * его, когда переход нужно перехватить — например ради плавного
   * скролла с `preventDefault()`.
   */
  onChange?: (next: string) => void;
}

export type SegmentedControlProps = RadioProps | CheckboxProps | ButtonProps | NavProps;

// ── Контейнер ──────────────────────────────────────────────────────────
// Скругление ОДНО ОБЩЕЕ и живёт на ряду: контейнер задаёт радиус и
// раздаёт внешние углы крайним ячейкам. `overflow: hidden` намеренно НЕ
// используется — он режет всё, что торчит за границу ряда (индикаторы в
// IndicatorAnchor, halo фокуса), на этом уже обжёгся SegmentedButton.
// Селекторы взяты по :first-child/:last-child, а не :first-of-type:
// ChipMUI рендерится то button, то div, то span, и type-селекторы бы
// разъехались.

const Row = styled(Box, {
  shouldForwardProp: prop => !String(prop).startsWith('$'),
})<{
  $fullWidth: boolean;
  $radius: number;
  $gap: SegmentedControlGap;
  $overflow: SegmentedControlOverflow;
}>(({theme, $fullWidth, $radius, $gap, $overflow}) => {
  const wrapping = $overflow === 'wrap';

  return {
    display: $fullWidth ? 'flex' : 'inline-flex',
    alignItems: 'stretch',
    gap: theme.spacing(GAP_FACTOR[$gap]),
    borderRadius: $radius,
    ...($fullWidth ? {width: '100%'} : {flexShrink: 0, width: 'fit-content'}),

    ...($overflow === 'scroll' && {
      // Ряд остаётся цельным, но получает собственный скролл вместо того,
      // чтобы распирать родителя. Плата за это — обрезка по краям: halo
      // фокуса у крайних ячеек упрётся в границу скролл-контейнера.
      maxWidth: '100%',
      overflowX: 'auto',
      flexWrap: 'nowrap',
    }),

    ...(wrapping && {
      flexWrap: 'wrap',
      width: 'auto',
      maxWidth: '100%',
    }),

    // Скругление ряда. В wrap конец строки заранее не известен, поэтому
    // внешние углы получает каждая ячейка. Радиус в обоих случаях —
    // ОДНО И ТО ЖЕ число, посчитанное от высоты одной ячейки; высота
    // выросшего в несколько строк контейнера на него не влияет.
    ...(wrapping
      ? {'& > *': {borderRadius: $radius}}
      : {
          '& > *:first-child': {
            borderTopLeftRadius: $radius,
            borderBottomLeftRadius: $radius,
          },
          '& > *:last-child': {
            borderTopRightRadius: $radius,
            borderBottomRightRadius: $radius,
          },
        }),
  };
});

// ── Компонент ──────────────────────────────────────────────────────────

export function SegmentedControl(props: SegmentedControlProps) {
  const {
    children,
    variant = 'segment',
    size = 'm',
    color = 'primary',
    idleColor = 'neutral',
    idleVariant: idleVariantProp,
    surface = 'surface-2',
    gap = 'tight',
    overflow = 'none',
    fullWidth = false,
    disabled = false,
    context: contextProp,
  } = props;
  const context = useSurfaceContext(contextProp);

  // chip-вид повторяет формулу одиночной пилюли: radius = height / 2.
  const radius = variant === 'chip' ? pillGeometry(size).height / 2 : RADIUS_M_PX;
  const idleVariant = idleVariantProp ?? SURFACE_VARIANT[surface];

  const ctxValue = useMemo<SegmentedControlContextValue>(() => {
    const shared = {
      size,
      color,
      idleColor,
      idleVariant,
      fullWidth,
      disabled,
    };

    if (props.mode === 'checkbox') {
      const {value, onChange} = props;
      return {
        ...shared,
        mode: 'checkbox',
        resolveSelected: cell => (cell == null ? false : value.includes(cell)),
        handleClick: (cell, ownOnClick) => {
          if (cell != null) {
            onChange(value.includes(cell) ? value.filter(x => x !== cell) : [...value, cell]);
          }
          ownOnClick?.();
        },
      };
    }

    if (props.mode === 'nav') {
      const {value, onChange} = props;
      return {
        ...shared,
        mode: 'nav',
        resolveSelected: cell => cell != null && cell === value,
        // Ячейка сама ссылка и переходит нативно. onChange нужен только
        // тем, кто перехватывает переход (плавный скролл и т.п.), поэтому
        // текущую ячейку повторно не объявляем.
        handleClick: (cell, ownOnClick) => {
          if (cell != null && cell !== value) onChange?.(cell);
          ownOnClick?.();
        },
      };
    }

    if (props.mode === 'button') {
      return {
        ...shared,
        mode: 'button',
        // В button-режиме группа не рулит выбором — выигрывает
        // собственный `selected` ячейки (если он есть).
        resolveSelected: () => false,
        handleClick: (_cell, ownOnClick) => {
          ownOnClick?.();
        },
      };
    }

    const {value, onChange} = props as RadioProps;
    return {
      ...shared,
      mode: 'radio',
      resolveSelected: cell => cell != null && cell === value,
      handleClick: (cell, ownOnClick) => {
        if (cell != null && cell !== value) onChange(cell);
        ownOnClick?.();
      },
    };
  }, [props, size, color, idleColor, idleVariant, fullWidth, disabled]);

  // ARIA-роль ряда. У nav её нет намеренно: это не набор контролов, а
  // список ссылок — оборачивай на месте использования в
  // `<nav aria-label='…'>`, иначе роль соврёт про назначение блока.
  let role: string | undefined = 'group';
  if (props.mode === 'radio' || props.mode === undefined) role = 'radiogroup';
  else if (props.mode === 'nav') role = undefined;

  return (
    <SegmentedControlContext.Provider value={ctxValue}>
      <Row
        role={role}
        aria-disabled={disabled || undefined}
        data-color-scheme={context}
        $fullWidth={fullWidth}
        $radius={radius}
        $gap={gap}
        $overflow={overflow}
      >
        {children}
      </Row>
    </SegmentedControlContext.Provider>
  );
}
